import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, List, Typography, Badge, Empty, Tooltip, message } from 'antd';
import {
  BugOutlined,
  CloseOutlined,
  ClearOutlined,
  PushpinOutlined,
  CheckCircleFilled,
  DisconnectOutlined,
  LoadingOutlined,
  WarningOutlined,
  CopyOutlined
} from '@ant-design/icons';
import {useWsClient} from "./useWsClient";
import { styled } from 'styled-components';

const { Text } = Typography;

const StyledDebugWindow = styled.div`
  .new-message {
    animation: fadeBackground 3s ease;
  }
  
  @keyframes fadeBackground {
    0% { background-color: #91caff; }
    100% { background-color: transparent; }
  }
`;

// 添加connectionStatus参数
// const DebugWindow = ({ messages = [], onClear, connectionStatus = 'disconnected' }) => {
const DebugWindow = ({  }) => {
  // 修改默认位置到右下角
  const [position, setPosition] = useState({ x: window.innerWidth - 320, y: 120 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isPinned, setIsPinned] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastMessageIndex, setLastMessageIndex] = useState(-1);

  const windowRef = useRef(null);
  const listRef = useRef(null);

  const [messages, setDebugMessages] = useState([]);
  const {connectionStatus, lastMessage} = useWsClient();
  const onClear = () => {
    setDebugMessages([]);
  };

  // 监听WebSocket消息并更新调试信息
  useEffect(() => {
    if (lastMessage) {
      try {
        const latestMessage = JSON.parse(lastMessage.data);
        setDebugMessages(prev => [...prev, {
          time: new Date().toLocaleTimeString(),
          type: latestMessage.type || 'info',
          message: typeof latestMessage === 'object' ? JSON.stringify(latestMessage) : latestMessage
        }]);
      } catch (error) {
        setDebugMessages(prev => [...prev.slice(0, 10), {
          time: new Date().toLocaleTimeString(),
          type: 'info',
          message: typeof lastMessage.data === 'string' ? lastMessage.data : lastMessage.data.toString()
        }]);
      }
    }
  }, [lastMessage]); // 只依赖 lastMessage

  // 当有新消息时，如果窗口折叠，增加未读计数
  useEffect(() => {
    if (messages.length > 0 && messages.length > lastMessageIndex + 1) {
      setLastMessageIndex(messages.length - 1);

      if (isCollapsed) {
        setUnreadCount(prev => prev + 1);
      }
    }
  }, [messages.length, isCollapsed]); // 移除 lastMessageIndex 依赖

  // 获取连接状态对应的图标和颜色
  // const getConnectionStatusIcon = () => {
  //   switch(connectionStatus) {
  //     case 'Open':
  //       return <CheckCircleFilled style={{ color: '#52c41a' }} />;
  //     case 'connecting':
  //       return <LoadingOutlined style={{ color: '#1890ff' }} />;
  //     case 'error':
  //       return <WarningOutlined style={{ color: '#ff4d4f' }} />;
  //     case 'disconnected':
  //     default:
  //       return <DisconnectOutlined style={{ color: '#faad14' }} />;
  //   }
  // };

  // 获取连接状态文本 - 使用 useMemo 避免不必要的重新计算
  const [connectionStatusText, ConnectionStatusIcon] = React.useMemo(() => {
    switch(connectionStatus) {
      case 'Open':
        return ['已连接', <CheckCircleFilled style={{ color: '#52c41a' }} />];
      case 'connecting':
        return ['连接中', <LoadingOutlined style={{ color: '#1890ff' }} />];
      case 'error':
        return ['连接错误', <WarningOutlined style={{ color: '#ff4d4f' }} />];
      case 'disconnected':
      default:
        return ['未连接', <DisconnectOutlined style={{ color: '#faad14' }} />];
    }
  }, [connectionStatus]);

  // 处理拖动开始
  const handleMouseDown = (e) => {
    if (isPinned) return;

    setIsDragging(true);
    const rect = windowRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });

    // 防止文本选择
    e.preventDefault();
  };

  // 处理拖动过程
  const handleMouseMove = (e) => {
    if (!isDragging) return;

    setPosition({
      x: e.clientX - dragOffset.x,
      y: e.clientY - dragOffset.y
    });
  };

  // 处理拖动结束
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // 切换固定状态
  const togglePin = () => {
    setIsPinned(!isPinned);
  };

  // 切换折叠状态
  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
    if (isCollapsed) {
      setUnreadCount(0);
    }
  };

  // 清除消息
  const clearMessages = () => {
    if (onClear) onClear();
    setLastMessageIndex(-1);
  };

  // 当消息列表更新时，滚动到顶部（因为现在是倒序显示）
  useEffect(() => {
    if (listRef.current && !isCollapsed) {
      listRef.current.scrollTop = 0;
    }
  }, [messages, isCollapsed]);

  // 添加全局鼠标事件监听
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // 复制消息到剪贴板
  const copyToClipboard = (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(
        () => {
          message.success('已复制到剪贴板');
        },
        (err) => {
          message.error('复制失败: ' + err);
        }
      );
    } else {
      // 备选方案：创建一个临时文本区域元素
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';  // 避免滚动到底部
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        const successful = document.execCommand('copy');
        if (successful) {
          message.success('已复制到剪贴板');
        } else {
          message.error('复制失败');
        }
      } catch (err) {
        message.error('复制失败: ' + err);
      }
      
      document.body.removeChild(textArea);
    }
  };

  return (
    <StyledDebugWindow
      ref={windowRef}
      className="fixed shadow-lg rounded-md z-50"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: isCollapsed ? 'auto' : '300px',
        cursor: isDragging ? 'grabbing' : (isPinned ? 'default' : 'grab'),
        opacity: isDragging ? 0.7 : 1,
        transition: isDragging ? 'none' : 'opacity 0.2s, width 0.3s'
      }}
    >
      {isCollapsed ? (
        <Badge count={unreadCount} overflowCount={99}>
          <Button
            type="primary"
            icon={<BugOutlined />}
            size="large"
            shape="circle"
            onClick={toggleCollapse}
            className="shadow-md"
          />
        </Badge>
      ) : (
        <Card
          size="small"
          title={
            <div
              className="flex items-center cursor-grab w-full"
              onMouseDown={handleMouseDown}
            >
              <div className="flex items-center">
                <BugOutlined className="mr-2" />
                调试信息
                <Tooltip title={connectionStatusText}>
                  <span className="ml-2">{ConnectionStatusIcon}</span>
                </Tooltip>
              </div>
            </div>
          }
          extra={
            <div className="flex space-x-1">
              <Button
                type={isPinned ? "primary" : "text"}
                size="small"
                icon={<PushpinOutlined />}
                onClick={togglePin}
              />
              <Button
                type="text"
                size="small"
                icon={<ClearOutlined />}
                onClick={clearMessages}
              />
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={toggleCollapse}
              />
            </div>
          }
          styles={{
              body: {padding: '8px', maxHeight: '300px', overflow: 'auto'}
          }}
          className="shadow-md"
        >
          <div ref={listRef} className="max-h-[300px] overflow-auto">
            {messages.length === 0 ? (
              <div className="text-center text-gray-400 py-4">暂无调试信息</div>
            ) : (
              <List
                size="small"
                dataSource={[...messages].reverse()} // 反转消息数组，使最新的消息在顶部
                renderItem={(item, index) => {
                  // 计算原始数组中的索引，用于确定是否是最新消息
                  const originalIndex = messages.length - 1 - index;
                  const isLatestMessage = originalIndex === lastMessageIndex;

                  return (
                    <List.Item
                      key={originalIndex}
                      className={`py-1 px-2 border-b border-gray-100 ${isLatestMessage ? 'new-message' : ''}`}
                      style={{
                        backgroundColor: isLatestMessage ? '#e6f7ff' : 'transparent',
                        transition: 'background-color 3s ease'
                      }}
                    >
                      <div className="w-full">
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                          <span>{item.time || new Date().toLocaleTimeString()}</span>
                          <span>{item.type || 'info'}</span>
                        </div>
                        <div className="flex justify-between items-start">
                          <Text
                            ellipsis={{ tooltip: item.message }}
                            className={`text-sm ${item.type === 'error' ? 'text-red-500' : ''} flex-1 mr-2`}
                          >
                            {item.message}
                          </Text>
                          <Button
                            type="text"
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={() => copyToClipboard(item.message)}
                            className="flex-none opacity-50 hover:opacity-100"
                          />
                        </div>
                      </div>
                    </List.Item>
                  );
                }}
              />
            )}
          </div>
        </Card>
      )}
    </StyledDebugWindow>
  );
};

// DebugWindow.whyDidYouRender = true;
export default DebugWindow;
